import { useEffect, useState, type ComponentType, type Dispatch, type SetStateAction } from "react";
import { useIsDirty } from "./useIsDirty.ts";
import { TabDialogActions } from "./TabDialogActions.tsx";

export interface TabPanelProps {
  rawSections: ReadonlyMap<string, string[][]>;
  onSave: (raw: ReadonlyMap<string, string[][]>) => void;
  onClose: () => void;
  /** Reports this panel's dirty state up to `ProjectDataDialog`, which feeds it to `Dialog.tsx`'s `isDirty` — this component sits below `Dialog` in the tree, so it can't set that prop directly. */
  onDirtyChange: (dirty: boolean) => void;
}

/**
 * Most `ProjectDataDialog.tsx` tab panels share the exact same shape: one
 * named-list `useState` seeded from `rawSections`, a selection,
 * `useIsDirty`+the dirty-reporting effect, an editor, and a save that writes
 * the (possibly edited) list back. 10+ panels re-implemented that
 * identically (P1 deep-review item 5) — `editorProps` is the only per-tab
 * thing that actually differs (each editor component names its own
 * main-list prop differently: `curves`, `series`, `packs`, etc.), so it's
 * the one piece callers still write by hand; everything else collapses into
 * this factory. `readExtra` covers the handful of tabs that also need one
 * extra, read-once, read-only reference list (e.g. LidControls' own
 * `pollutants` picker). Kept in its own file, not `ProjectDataDialog.tsx`
 * itself — that file's only other export is a component, and mixing in a
 * non-component export there defeats React Fast Refresh for the whole file.
 */
export function makeSimpleTabPanel<T extends { name: string }, Extra, P extends object>(config: {
  read: (raw: ReadonlyMap<string, string[][]>) => T[];
  write: (raw: ReadonlyMap<string, string[][]>, items: T[]) => ReadonlyMap<string, string[][]>;
  readExtra?: (raw: ReadonlyMap<string, string[][]>) => Extra;
  Editor: ComponentType<P>;
  editorProps: (
    items: T[],
    setItems: Dispatch<SetStateAction<T[]>>,
    selected: string | null,
    onSelect: Dispatch<SetStateAction<string | null>>,
    extra: Extra
  ) => P;
}): ComponentType<TabPanelProps> {
  return function SimpleTabPanel({ rawSections, onSave, onClose, onDirtyChange }: TabPanelProps) {
    const [items, setItems] = useState<T[]>(() => config.read(rawSections));
    const [selected, setSelected] = useState<string | null>(() => items[0]?.name ?? null);
    const [extra] = useState<Extra>(() => config.readExtra?.(rawSections) as Extra);
    const dirty = useIsDirty(items);
    useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
    const Editor = config.Editor;
    return (
      <>
        <Editor {...config.editorProps(items, setItems, selected, setSelected, extra)} />
        <TabDialogActions
          onSave={() => onSave(config.write(rawSections, items))}
          onClose={onClose}
        />
      </>
    );
  };
}
